import { Link } from "@tanstack/react-router";
import { useState } from "react";

import Claimed from "@/components/claim-status/claimed";
import Partner from "@/components/claim-status/partner";
import Unclaimed from "@/components/claim-status/unclaimed";
import CommunityScoreWheel from "@/components/community-score-wheel";
import GameCategories from "@/components/game-categories";
import { ChevronRightIcon } from "@/components/icons/chevrons";
import PlayedButton from "@/components/played-button";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toSentenceCase } from "@/helpers/formatters";
import { cn } from "@/lib/utils";

import type { GameType, UserGameType } from "../types";
import Description from "./description";
import HighlightProperties from "./highlight-properties";
import { CouponsIcon } from "./icons";
import TagPlayers from "./tag-players";

interface DetailsProps {
  game: GameType;
  isOpen: boolean;
  onReviewsClick(): void;
  reviewCount: number;
  userGame?: UserGameType;
}

export default function Details({
  game,
  isOpen,
  onReviewsClick,
  reviewCount,
  userGame,
}: DetailsProps) {
  const [justAdded, setJustAdded] = useState(false);

  const handleCompleted = () => setJustAdded(true);

  const { communityRatingCount, communityScorePercentages, location } = game;

  return (
    <>
      <div className="flex flex-col justify-end md:h-127 md:pb-8">
        <h1 className="font-semibold text-24/[1.25em] text-white md:text-60/[1em]">
          {game.name}
          {isOpen && !!game.coupons.length && (
            <CouponsIcon className="ml-5 inline-block h-3 w-[21px] md:h-7.5 md:w-[53px]" />
          )}
        </h1>
        {(!!communityRatingCount || !!reviewCount) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-2.5 md:mt-4.5 md:gap-x-3">
            {!!communityRatingCount && (
              <div className="flex flex-wrap items-center gap-x-[5px] md:gap-x-3">
                {communityScorePercentages && (
                  <CommunityScoreWheel
                    className="size-4 md:size-7.5"
                    scores={communityScorePercentages}
                  />
                )}
                {!!game.communityScoreBucket && (
                  <p className="text-16 text-white leading-[1.2em] md:font-semibold md:text-18">
                    {toSentenceCase(game.communityScoreBucket)}{" "}
                    <span className="text-pale-lavender">
                      ({communityRatingCount} ratings)
                    </span>
                  </p>
                )}
              </div>
            )}
            {!!reviewCount && (
              <Button
                className="text-16 text-white leading-[1.2em] hover:underline md:text-18"
                onClick={onReviewsClick}
              >
                {reviewCount} review{reviewCount !== 1 && "s"}
              </Button>
            )}
          </div>
        )}
        <div
          className={cn(
            "mt-2.5 flex items-center md:mt-4.5",
            game.location.isClaimed ? "gap-2.5" : "gap-3.5"
          )}
        >
          {game.location.isPartnerLite ? (
            <Partner className="size-4.5" />
          ) : (
            game.location.isClaimed && <Claimed />
          )}
          <h2 className="border-transparent border-b font-medium text-14 text-white leading-[1.2em] transition-colors hover:border-white md:text-18">
            <Link to={`/location/${location.company.slug}/${location.slug}`}>
              {location.displayName || location.company.name}
              <ChevronRightIcon className="-mt-0.5 ml-[11px] inline-block aspect-[1/2] h-auto w-1.5" />
            </Link>
          </h2>
          {!game.location.isClaimed && <Unclaimed />}
        </div>
        <GameCategories
          categories={[game.primaryCategory, ...game.secondaryCategories]}
          className="mt-5 gap-1.5 md:mt-6"
        />
      </div>
      <Separator className="mt-5 mb-6 bg-white/20 md:hidden" />
      <div
        className={cn(
          "mb-5 rounded-[20px] md:mb-0 md:bg-white/5 md:p-7.5",
          game.hasBookings ? "md:mt-18.5" : "md:mt-7.5"
        )}
      >
        <PlayedButton
          game={{ ...game, ...userGame }}
          onCompleted={handleCompleted}
        />
        {userGame?.isCompleted && (
          <div className="mt-5 flex flex-wrap gap-y-5">
            {!!userGame.myEscape && (
              <TagPlayers game={userGame} key={userGame.myEscape?.id} />
            )}
            <Link
              className="ml-auto flex h-[45px] items-center whitespace-nowrap rounded-[23px] border border-white/50 px-5 font-medium text-14 text-white transition-colors hover:bg-white/10 md:text-16"
              to={`/experience/${game.id}/${userGame.myEscape?.id}`}
            >
              {justAdded ? "Add details" : "Edit details"}
              <svg
                aria-hidden="true"
                className="ml-[5px] h-[11px] w-3.5 text-white"
                viewBox="0 0 14 11"
              >
                <path
                  d="M13.057 5.324a.646.646 0 00-.212-.478L8.579.58a.748.748 0 00-.24-.157.634.634 0 00-.697.123.603.603 0 00-.177.444c0 .087.016.171.048.253.032.082.075.15.13.205l1.449 1.47 2.488 2.27.185-.376-2.167-.13H1.586a.639.639 0 00-.472.184.62.62 0 00-.177.458c0 .192.059.349.177.472.123.118.28.178.472.178h8.012l2.167-.137-.185-.362L9.092 7.73l-1.45 1.477a.658.658 0 00-.178.458.6.6 0 00.179.444.634.634 0 00.458.178.634.634 0 00.458-.205l4.286-4.272a.653.653 0 00.212-.486z"
                  fill="currentColor"
                />
              </svg>
            </Link>
          </div>
        )}
      </div>
      <div className="flex flex-col items-stretch gap-4">
        <Description description={game.description} />
        <HighlightProperties
          difficulty={game.difficulty}
          minutes={game.minutes}
          playersMax={game.playersMax}
          playersMin={game.playersMin}
          tags={game.tags}
        />
      </div>
    </>
  );
}
